// Typed client for the Xpedition2 API. Paths and bodies come from src/api/schema.ts (generated), so a
// change to the API's contract shows up here as a type error.
import createClient, { type Middleware } from 'openapi-fetch';
import { config } from '@/config';
import type { paths } from './schema';

// The current access token. AuthProvider keeps this up to date as tokens are issued and renewed.
let currentToken: string | undefined;

/** The current access token, for connections that send it themselves (the real-time hub). */
export const getAccessToken = () => currentToken;

export function setAccessToken(token: string | undefined) {
  currentToken = token;
}

const bearer: Middleware = {
  onRequest({ request }) {
    if (currentToken) request.headers.set('Authorization', `Bearer ${currentToken}`);
    return request;
  },
};

// "Premium only" (403, code 1050, details: [feature]): whoever listens (the Subscribe dialog) offers Premium.
let premiumRequired: ((feature: string | undefined) => void) | undefined;

export function onPremiumRequired(listener: typeof premiumRequired) {
  premiumRequired = listener;
}

async function checkPremium(response: Response) {
  if (response.status !== 403 || !premiumRequired) return;
  const body = (await response
    .clone()
    .json()
    .catch(() => undefined)) as { message?: string; details?: string[] } | undefined;
  if (body?.message === '1050') premiumRequired(body.details?.[0]);
}

const premium: Middleware = {
  async onResponse({ response }) {
    await checkPremium(response);
    return response;
  },
};

export const api = createClient<paths>({ baseUrl: config.apiUrl });
api.use(bearer, premium);

/** The Authorization header, for requests made other than with apiFetch (map tiles). */
export function authHeaders(): Record<string, string> {
  return currentToken ? { Authorization: `Bearer ${currentToken}` } : {};
}

/** A plain fetch against the API with the access token, for binary bodies the typed client doesn't cover. */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  if (currentToken) headers.set('Authorization', `Bearer ${currentToken}`);
  const response = await fetch(`${config.apiUrl.replace(/\/$/, '')}${path}`, { ...init, headers });
  await checkPremium(response);
  return response;
}
